import { memo } from "react";
import { ImLinkedin } from "react-icons/im";
import { HiOutlineMail, HiOutlineLocationMarker } from "react-icons/hi";
import { HiOutlineBuildingOffice2, HiOutlineUsers } from "react-icons/hi2";

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "?";

const MetaItem = ({ icon: Icon, children, muted }) => (
  <span className="flex min-w-0 items-center gap-1.5">
    <Icon className="h-4 w-4 shrink-0 text-app-muted" aria-hidden="true" />
    <span className={`truncate ${muted ? "italic text-app-muted" : ""}`}>
      {children}
    </span>
  </span>
);

const UserCard = ({ user }) => {
  const emails = [user.email, user.email2].filter(Boolean);

  return (
    <article className="group flex h-full flex-col rounded-xl border border-app-primary bg-app-card p-4 transition-all duration-200 hover:-translate-y-px hover:border-[#2470B7]/30 hover:bg-app-secondary hover:shadow-md">
      {/* Identity */}
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2470B7]/10 text-sm font-semibold text-[#2470B7] ring-1 ring-[#2470B7]/15"
          aria-hidden="true"
        >
          {getInitials(user.name)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <h3
                className="truncate text-[15px] font-semibold leading-6 text-app-heading"
                title={user.name}
              >
                {user.name || "Unknown contact"}
              </h3>
              {user.linkedIn && (
                <a
                  href={user.linkedIn}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${user.name || "Contact"} on LinkedIn`}
                  className="shrink-0 rounded text-[#2470B7] opacity-80 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2470B7]/40"
                >
                  <ImLinkedin className="h-3.5 w-3.5" />
                </a>
              )}
            </div>

            {user.industry && (
              <span
                className="max-w-[45%] shrink-0 truncate rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20"
                title={user.industry}
              >
                {user.industry}
              </span>
            )}
          </div>

          <p
            className="truncate text-sm text-app-secondary"
            title={user.jobTitle}
          >
            {user.jobTitle || (
              <span className="italic text-app-muted">No job title</span>
            )}
          </p>

          <p
            className="mt-0.5 flex min-w-0 items-center gap-1.5 text-sm font-medium text-app-primary"
            title={user.companyName}
          >
            <HiOutlineBuildingOffice2
              className="h-4 w-4 shrink-0 text-app-muted"
              aria-hidden="true"
            />
            <span className="truncate">
              {user.companyName || (
                <span className="font-normal italic text-app-muted">
                  No company
                </span>
              )}
            </span>
          </p>
        </div>
      </div>

      {/* Contact + metadata */}
      <div className="mt-4 space-y-2 border-t border-app-secondary pt-3 text-[13px] text-app-secondary">
        {emails.length ? (
          emails.map((email) => (
            <div key={email} className="flex min-w-0 items-center gap-1.5">
              <HiOutlineMail
                className="h-4 w-4 shrink-0 text-[#2470B7]"
                aria-hidden="true"
              />
              <span
                className="truncate font-medium text-app-primary"
                title={email}
              >
                {email}
              </span>
            </div>
          ))
        ) : (
          <MetaItem icon={HiOutlineMail} muted>
            No email provided
          </MetaItem>
        )}

        <div className="flex min-w-0 items-center gap-2 text-app-muted">
          <MetaItem icon={HiOutlineLocationMarker} muted={!user.location}>
            {user.location || "No location"}
          </MetaItem>
          <span aria-hidden="true" className="text-app-muted">
            ·
          </span>
          <MetaItem icon={HiOutlineUsers} muted={!user.headAccount}>
            {!user.headAccount
              ? "Size N/A"
              : /self[\s-]?employed/i.test(user.headAccount)
                ? user.headAccount
                : `${user.headAccount} employees`}
          </MetaItem>
        </div>
      </div>
    </article>
  );
};

export default memo(UserCard);
